/**
 * /workspace/catalog/manual — 3-step guided Product Wizard.
 *
 * Step 1: Basics (title, category, description)
 * Step 2: Pricing & stock (price, cost, sku, stock)
 * Step 3: Review & launch → POST /api/products
 */
import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Sparkles, Package, Wallet, Rocket } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

const CATEGORIES = [
  "electronics", "apparel", "beauty", "home-kitchen", "grocery",
  "health-wellness", "toys-baby", "sports-outdoors", "handmade-artisan",
  "digital-services", "auto-industrial", "books-media",
];

interface Draft {
  title: string; category: string; description: string;
  price: string; cost_price: string; sku: string; stock_on_hand: string;
}

export default function CatalogManual() {
  const [step, setStep] = React.useState(1);
  const [d, setD] = React.useState<Draft>({
    title: "", category: "handmade-artisan", description: "",
    price: "", cost_price: "", sku: "", stock_on_hand: "",
  });
  const nav = useNavigate();
  const qc = useQueryClient();

  const canNext =
    (step === 1 && d.title.trim().length >= 3 && d.category) ||
    (step === 2 && Number(d.price) > 0);

  const create = useMutation({
    mutationFn: async () => (await api.post("/products", {
      title: d.title.trim(),
      category: d.category,
      description: d.description.trim() || undefined,
      price: Number(d.price),
      cost_price: d.cost_price ? Number(d.cost_price) : undefined,
      sku: d.sku.trim() || undefined,
      stock_on_hand: d.stock_on_hand ? Number(d.stock_on_hand) : 0,
    })).data,
    onSuccess: () => {
      toast.success(`${d.title} is live on your Shop 🎉`);
      qc.invalidateQueries({ queryKey: ["ws-module", "catalog-manual"] });
      qc.invalidateQueries({ queryKey: ["sell-inventory"] });
      nav("/workspace/sell/inventory");
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Create failed"),
  });

  return (
    <DashboardLayout title="Add a product" subtitle="Guided wizard — title, pricing, launch. Land on the Shop in 60 seconds.">
      {/* Stepper */}
      <div className="flex items-center justify-between max-w-2xl mx-auto mb-6" data-testid="wizard-stepper">
        {[
          { n: 1, label: "Basics",   icon: Package },
          { n: 2, label: "Pricing",  icon: Wallet },
          { n: 3, label: "Launch",   icon: Rocket },
        ].map((s, i, arr) => {
          const Icon = s.icon;
          const active = step === s.n; const done = step > s.n;
          return (
            <React.Fragment key={s.n}>
              <div className="flex flex-col items-center gap-1" data-testid={`wizard-step-${s.n}`}>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${
                  done  ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                : active ? "bg-[color:var(--sb-saffron,#F97316)] text-white shadow-lg shadow-orange-500/30 scale-110"
                        : "bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-muted)]"
                }`}>
                  {done ? <CheckCircle2 className="h-5 w-5"/> : <Icon className="h-4 w-4" strokeWidth={2.5}/>}
                </div>
                <div className={`text-[10px] uppercase tracking-widest font-semibold ${active ? "text-[color:var(--sb-saffron,#F97316)]" : "text-[color:var(--sg-muted)]"}`}>{s.label}</div>
              </div>
              {i < arr.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? "bg-emerald-500" : "bg-[color:var(--sg-border)]"}`}/>}
            </React.Fragment>
          );
        })}
      </div>

      <div className="max-w-2xl mx-auto rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6 sm:p-8" data-testid="wizard-panel">
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Product title</label>
              <Input data-testid="wizard-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} className="mt-1 h-11" placeholder="Handloom Kurta · Saffron block-print"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Category</label>
              <select data-testid="wizard-category" value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} className="mt-1 h-11 w-full rounded-md border border-[color:var(--sg-border)] bg-transparent px-3 text-sm capitalize">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Description (optional)</label>
              <Textarea data-testid="wizard-description" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} rows={4} className="mt-1" placeholder="What makes it special? Materials, craft, sizing — details help SibaScore rank it higher."/>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Selling price (₹)</label>
                <Input data-testid="wizard-price" type="number" min={1} value={d.price} onChange={(e) => setD({ ...d, price: e.target.value })} className="mt-1 h-11"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Cost price (₹) · optional</label>
                <Input data-testid="wizard-cost" type="number" min={0} value={d.cost_price} onChange={(e) => setD({ ...d, cost_price: e.target.value })} className="mt-1 h-11"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">SKU · optional</label>
                <Input data-testid="wizard-sku" value={d.sku} onChange={(e) => setD({ ...d, sku: e.target.value })} className="mt-1 h-11" placeholder="Auto-generated if blank"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">Starting stock</label>
                <Input data-testid="wizard-stock" type="number" min={0} value={d.stock_on_hand} onChange={(e) => setD({ ...d, stock_on_hand: e.target.value })} className="mt-1 h-11"/>
              </div>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-review">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[color:var(--sb-saffron,#F97316)] flex items-center gap-1.5"><Sparkles className="h-3 w-3"/> Ready to launch</div>
            <div className="font-serif-display text-2xl font-bold text-[color:var(--sg-fg)]">{d.title || "(untitled)"}</div>
            <div className="text-xs text-[color:var(--sg-muted)] capitalize">Category · {d.category}</div>
            {d.description && <p className="text-sm text-[color:var(--sg-fg)] leading-relaxed">{d.description}</p>}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[color:var(--sg-border)]">
              <Row label="Price"    value={`₹${Number(d.price || 0).toLocaleString("en-IN")}`}/>
              <Row label="Cost"     value={d.cost_price ? `₹${Number(d.cost_price).toLocaleString("en-IN")}` : "—"}/>
              <Row label="SKU"      value={d.sku || "auto"}/>
              <Row label="Stock"    value={d.stock_on_hand || "0"}/>
            </div>
            <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200">
              After launch, SibaScore will auto-generate an AI score within 2 minutes. Live in Shop immediately.
            </div>
          </div>
        )}
        <div className="mt-6 flex items-center justify-between">
          <Button
            data-testid="wizard-back"
            variant="outline"
            disabled={step === 1}
            onClick={() => setStep(step - 1)}
            className="h-10"
          >
            <ChevronLeft className="h-4 w-4 mr-1"/> Back
          </Button>
          {step < 3 ? (
            <Button
              data-testid="wizard-next"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              className="h-10 bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white font-semibold"
            >
              Next <ChevronRight className="h-4 w-4 ml-1"/>
            </Button>
          ) : (
            <Button
              data-testid="wizard-launch"
              disabled={create.isPending}
              onClick={() => create.mutate()}
              className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {create.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Launching…</> : <><Rocket className="h-4 w-4 mr-1.5"/> Launch on Shop</>}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 text-center">
        <Link to="/workspace/catalog/bulk" className="text-xs text-[color:var(--sg-muted)] hover:text-[color:var(--sb-saffron,#F97316)] underline">
          Or bulk-import from CSV →
        </Link>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)]">{label}</div>
      <div className="text-sm text-[color:var(--sg-fg)] font-semibold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
